import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["select"]

  connect() {
    this.options = this.buildOptions()
    this.handleDocumentClick = this.closeOnOutsideClick.bind(this)
    this.handleSelectChange = this.syncInput.bind(this)

    this.selectTarget.classList.add("hidden")
    this.selectTarget.addEventListener("change", this.handleSelectChange)
    document.addEventListener("click", this.handleDocumentClick)

    this.renderCombobox()
    this.syncInput()
  }

  disconnect() {
    this.selectTarget.classList.remove("hidden")
    this.selectTarget.removeEventListener("change", this.handleSelectChange)
    document.removeEventListener("click", this.handleDocumentClick)
    this.combobox?.remove()
  }

  filter() {
    this.renderOptions(this.input.value)
    this.open()
  }

  choose(event) {
    const value = event.currentTarget.dataset.value
    this.selectTarget.value = value
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
      return
    }

    if (event.key === "Enter") {
      const firstOption = this.list.querySelector("[data-value]")
      if (!firstOption || this.list.classList.contains("hidden")) return

      event.preventDefault()
      firstOption.click()
    }
  }

  buildOptions() {
    return Array.from(this.selectTarget.children).flatMap((child) => {
      if (child.tagName === "OPTGROUP") {
        return Array.from(child.children).map((option) => this.optionFromElement(option, child.label))
      }

      return [this.optionFromElement(child, "")]
    })
  }

  optionFromElement(option, group) {
    return {
      group,
      label: option.textContent.trim(),
      value: option.value
    }
  }

  renderCombobox() {
    this.combobox = document.createElement("div")
    this.combobox.className = "relative mt-2"
    this.combobox.innerHTML = `
      <input type="text" class="block w-full rounded-lg border border-stone-300 bg-white px-4 py-3 text-stone-900 shadow-sm focus:border-stone-500 focus:outline-none focus:ring-2 focus:ring-stone-200" autocomplete="off">
      <div class="absolute z-20 mt-1 hidden max-h-72 w-full overflow-auto rounded-lg border border-stone-300 bg-white py-2 text-sm shadow-lg"></div>
    `

    this.input = this.combobox.querySelector("input")
    this.list = this.combobox.querySelector("div")
    this.input.addEventListener("focus", () => this.open())
    this.input.addEventListener("input", () => this.filter())
    this.input.addEventListener("keydown", (event) => this.handleKeydown(event))

    this.selectTarget.insertAdjacentElement("afterend", this.combobox)
    this.renderOptions()
  }

  renderOptions(query = "") {
    const normalizedQuery = query.trim().toLowerCase()
    const matches = this.options.filter((option) => this.matches(option, normalizedQuery)).slice(0, 80)
    let currentGroup = null

    if (matches.length === 0) {
      this.list.innerHTML = `<div class="px-4 py-3 text-stone-500">No matching timezones</div>`
      return
    }

    this.list.innerHTML = matches.map((option) => {
      const groupHeading = option.group && option.group !== currentGroup
        ? `<div class="px-4 pb-1 pt-3 text-xs font-bold uppercase tracking-wide text-stone-500">${this.escapeHtml(option.group)}</div>`
        : ""
      currentGroup = option.group || currentGroup

      return `
        ${groupHeading}
        <button type="button" class="block w-full px-4 py-2 text-left text-stone-800 hover:bg-stone-100 focus:bg-stone-100 focus:outline-none" data-value="${this.escapeHtml(option.value)}">
          ${this.escapeHtml(option.label)}
        </button>
      `
    }).join("")

    this.list.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", (event) => this.choose(event))
    })
  }

  matches(option, query) {
    if (!query) return true

    return [option.group, option.label, option.value].some((value) => value.toLowerCase().includes(query))
  }

  syncInput() {
    const option = this.options.find((option) => option.value === this.selectTarget.value)
    this.input.value = option?.label || ""
    this.renderOptions()
  }

  open() {
    this.list.classList.remove("hidden")
    this.renderOptions(this.input.value)
  }

  close() {
    this.list.classList.add("hidden")
    this.syncInput()
  }

  closeOnOutsideClick(event) {
    if (this.element.contains(event.target)) return

    this.close()
  }

  escapeHtml(value) {
    return value.replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#39;"
    })[character])
  }
}
